import { CalendarEvent } from '../../../hooks/useCalendarEvents';
import { EventBadge } from './EventBadge';

interface CalendarMonthViewProps {
  year: number;
  month: number;
  eventsByDate: Record<string, CalendarEvent[]>;
  onDayClick: (date: string) => void;
  onEventClick: (event: CalendarEvent) => void;
}

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export const CalendarMonthView = ({
  year, month, eventsByDate, onDayClick, onEventClick,
}: CalendarMonthViewProps) => {
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');

  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const daysInPrevMonth = new Date(year, month - 1, 0).getDate();

  const cells: { date: string; day: number; currentMonth: boolean }[] = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrevMonth - i;
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    cells.push({ date: `${prevYear}-${pad(prevMonth)}-${pad(d)}`, day: d, currentMonth: false });
  }

  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: `${year}-${pad(month)}-${pad(d)}`, day: d, currentMonth: true });
  }

  const remaining = (7 - (cells.length % 7)) % 7;
  for (let d = 1; d <= remaining; d++) {
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    cells.push({ date: `${nextYear}-${pad(nextMonth)}-${pad(d)}`, day: d, currentMonth: false });
  }

  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="grid grid-cols-7 border-b border-gray-100">
        {WEEKDAYS.map((d) => (
          <div key={d} className="py-3 text-center text-[11px] font-600 text-gray-400 uppercase tracking-wider">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((cell, idx) => {
          const isToday = cell.date === todayStr;
          const events = eventsByDate[cell.date] ?? [];
          const hasMore = events.length > 2;

          return (
            <div
              key={idx}
              onClick={() => cell.currentMonth && onDayClick(cell.date)}
              className={`min-h-[100px] p-1.5 border-b border-r border-gray-50 relative transition-colors ${
                cell.currentMonth
                  ? 'bg-white hover:bg-gray-50/50 cursor-pointer'
                  : 'bg-gray-50/30 cursor-default'
              } ${idx % 7 === 6 ? 'border-r-0' : ''}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`text-[12px] font-600 w-6 h-6 flex items-center justify-center rounded-full ${
                    isToday
                      ? 'bg-emerald-500 text-white'
                      : cell.currentMonth
                      ? 'text-gray-700'
                      : 'text-gray-300'
                  }`}
                >
                  {cell.day}
                </span>
              </div>

              <div className="space-y-0.5">
                {events.slice(0, 2).map((evt) => (
                  <EventBadge
                    key={evt.id}
                    event={evt}
                    compact
                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); onEventClick(evt); }}
                  />
                ))}
                {hasMore && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onEventClick(events[2]); }}
                    className="text-[10px] text-gray-400 font-500 px-1 cursor-pointer hover:text-gray-600 w-full text-left whitespace-nowrap"
                  >
                    +{events.length - 2} mais
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
